"use strict";

import {OL3Parser as jstsOL3Parser} from "jsts/org/locationtech/jts/io";
import {GeometryFactory as jstsGeometryFactory} from "jsts/org/locationtech/jts/geom";
import {
	BufferOp as jstsBufferOp,
	BufferParameters as jstsBufferParameters
} from "jsts/org/locationtech/jts/operation/buffer";
import {IsValidOp as jstsIsValidOp} from "jsts/org/locationtech/jts/operation/valid";
import {
	Centroid as jstsCentroid,
	ConvexHull as jstsConvexHull
} from "jsts/org/locationtech/jts/algorithm";
import { OverlayOp as jstsOverlayOp } from "jsts/org/locationtech/jts/operation/overlay";
import UnionOp from "jsts/org/locationtech/jts/operation/union/UnionOp";
import * as olGeom from "ol/geom";
import * as olExtent from "ol/extent";


/**
 * @classdesc
 * Geometrie-Prozessor auf Basis der Openlayers Kartenbibliothek.
 *
 * @class
 * @extends gc.gis.geom.GCGeometryProcessor
 * @memberof gc.gis.geom
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 */
gc.gis.geom.OLGeometryProcessor = function()
{
    gc.gis.geom.GCGeometryProcessor.call(this);

	/**
	 *
	 * @type {jsts.io.OL3Parser}
	 * @private
	 */
	this._jtsParser = new jstsOL3Parser(undefined, undefined);
	this._jtsParser.inject(
		olGeom.Point,
		olGeom.LineString,
		olGeom.LinearRing,
		olGeom.Polygon,
		olGeom.MultiPoint,
		olGeom.MultiLineString,
		olGeom.MultiPolygon,
		olGeom.GeometryCollection
	);

	/**
	 * @type {jsts.geom.GeometryFactory}
	 * @private
	 */
	this._jtsGeometryFactory = new jstsGeometryFactory();

	/**
	 * @type {gc.gis.geom.OLGeometryFactory}
	 * @private
	 */
	this._geometryFactory = new gc.gis.geom.OLGeometryFactory();
};
gc.gis.geom.OLGeometryProcessor.prototype = Object.create(gc.gis.geom.GCGeometryProcessor.prototype);

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.rotate = function(geometry, angle, opt_anchor)
{
	let ret,
		ol_geom = geometry.getImpl();

	ol_geom = ol_geom.clone();
	if (!opt_anchor)
	{
		if (ol_geom instanceof olGeom.Circle)
		{
			opt_anchor = ol_geom.getCenter();
		}
		else
		{
			opt_anchor = olExtent.getCenter(ol_geom.getExtent());
		}
	}
	ol_geom.rotate(angle, opt_anchor);

	ret = this._geometryFactory.createGeometry(ol_geom);
	ret.setCRS(geometry.getCRS());

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.scale = function(geometry, sx, sy)
{
	let ret,
		ol_geom = geometry.getImpl();

	ol_geom = ol_geom.clone();
	ol_geom.scale(sx, sy);

	ret = this._geometryFactory.createGeometry(ol_geom);
	ret.setCRS(geometry.getCRS());

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.translate = function(geometry, deltaX, deltaY)
{
	let ret,
		ol_geom = geometry.getImpl();

	ol_geom = ol_geom.clone();
	ol_geom.translate(deltaX, deltaY);

	ret = this._geometryFactory.createGeometry(ol_geom);
	ret.setCRS(geometry.getCRS());

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.buffer = function(geometry, distance, quadrantSegments, endCapStyle)
{
	quadrantSegments = quadrantSegments || gc.gis.geom.GCGeometryOperationParameters.BUFFER_DEFAULT_QUADRANT_SEGMENTS;

	switch (endCapStyle)
	{
		case gc.gis.geom.GCGeometryOperationParameters.BUFFER_CAP_FLAT:
			endCapStyle = jstsBufferParameters.CAP_FLAT;
			break;
		case gc.gis.geom.GCGeometryOperationParameters.BUFFER_CAP_SQUARE:
			endCapStyle = jstsBufferParameters.CAP_SQUARE;
			break;
		default:
			endCapStyle = jstsBufferParameters.CAP_ROUND;
	}

	let ret = null,
		jsts_geom,
		radius;

	if (geometry instanceof gc.gis.geom.GCCircle)
	{
		radius = geometry.getImpl().getRadius();
		if (radius + distance > 0.0)
		{
			ret = this._geometryFactory.createCircle(
				new olGeom.Circle(
					geometry.getImpl().getCenter(),
					geometry.getImpl().getRadius() + distance)
			);
		}
	}
	else
	{
		jsts_geom = this._toJstsGeometry(geometry);
		if (!jsts_geom.isEmpty())
		{
			jsts_geom = jstsBufferOp.bufferOp(jsts_geom, distance, quadrantSegments, endCapStyle);
			if (!jsts_geom.isEmpty())
			{
				ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_geom));
			}
		}
	}

	if (ret)
	{
		ret.setCRS(geometry.getCRS());
	}

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.centroid = function(geometry)
{
	let jsts_geom, centroid;

	if (geometry instanceof gc.gis.geom.GCCircle)
	{
		centroid = this._geometryFactory.createPoint(new olGeom.Point(geometry.getImpl().getCenter()));
	}
	else
	{
		jsts_geom = this._toJstsGeometry(geometry);
		centroid = jstsCentroid.getCentroid(jsts_geom);
		centroid = this._jstsPointToCoord(centroid);
		centroid = this._geometryFactory.createPoint(new olGeom.Point(centroid));
	}

	centroid.setCRS(geometry.getCRS());

	return centroid;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.convexHull = function(geometry)
{
	let ret, jsts_geom;

	if (geometry instanceof gc.gis.geom.GCCircle)
	{
		ret = this._geometryFactory.createCircle(
			new olGeom.Circle(geometry.getImpl().getCenter(), geometry.getImpl().getRadius())
		);
	}
	else
	{
		jsts_geom = this._jtsParser.read(geometry.getImpl());
		jsts_geom = new jstsConvexHull(jsts_geom).getConvexHull();

		ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_geom));
	}

	ret.setCRS(geometry.getCRS());

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.convexHullList = function(geometries)
{
	let ret = null,
		jsts_geom,
		jsts_collection;

	this._validateCrsList(geometries);

	jsts_collection = this._toJstsGeometryCollection(geometries);
	jsts_geom = new jstsConvexHull(jsts_collection).getConvexHull();

	if (!jsts_geom.isEmpty())
	{
		ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_geom));
		ret.setCRS(geometries[0].getCRS());
	}

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.isValid = function(geometry)
{
	if (geometry instanceof gc.gis.geom.GCCircle)
	{
		return geometry.getImpl().getCenter().length > 0 && geometry.getImpl().getRadius() > 0.0;
	}

	let jsts_geom = this._jtsParser.read(geometry.getImpl()),
		is_valid_op = new jstsIsValidOp(jsts_geom);

	return is_valid_op.isValid();
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.checkValid = function(geometry)
{
	// TODO Kreis?
	let jsts_geom = this._jtsParser.read(geometry.getImpl()),
		is_valid_op = new jstsIsValidOp(jsts_geom),
		error = is_valid_op.getValidationError(),
		coord = [];

	if (error)
	{
		coord.push(error.getCoordinate()['x']);
		coord.push(error.getCoordinate()['y']);
		if (error.getCoordinate()['z'])
		{
			coord.push(error.getCoordinate()['z']);
		}

		return {
			message: error.getMessage(),
			coordinate: coord
		}
	}

	return null;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.difference = function(geometryA, geometryB)
{
	let ret = null,
		jsts_geom_a = this._toJstsGeometry(geometryA),
		jsts_geom_b = this._toJstsGeometry(geometryB);

	this._validateCrs(geometryA, geometryB);

	jsts_geom_a = jstsOverlayOp.difference(jsts_geom_a, jsts_geom_b);
	if (!jsts_geom_a.isEmpty())
	{
		ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_geom_a));
		ret.setCRS(geometryA.getCRS());
	}

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.differenceList = function(geometryA, geometriesB)
{
	let ret = null,
		i, ii,
		jsts_geom_a = this._toJstsGeometry(geometryA),
		jsts_geom_b;

	this._validateCrsWithList(geometryA, geometriesB);

	for (i = 0, ii = geometriesB.length; i < ii; i++)
	{
		jsts_geom_b = this._toJstsGeometry(geometriesB[i]);
		jsts_geom_a = jstsOverlayOp.difference(jsts_geom_a, jsts_geom_b);
	}

	if (!jsts_geom_a.isEmpty())
	{
		ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_geom_a));
		ret.setCRS(geometryA.getCRS());
	}

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.intersection = function(geometryA, geometryB)
{
	let ret = null,
		jsts_geom_a = this._toJstsGeometry(geometryA),
		jsts_geom_b = this._toJstsGeometry(geometryB);

	this._validateCrs(geometryA, geometryB);

	jsts_geom_a = jstsOverlayOp.intersection(jsts_geom_a, jsts_geom_b);
	if (!jsts_geom_a.isEmpty())
	{
		ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_geom_a));
		ret.setCRS(geometryA.getCRS());
	}

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.intersectionList = function(geometryA, geometriesB)
{
	let ret = null, i, ii,
		jsts_geom_a = this._toJstsGeometry(geometryA),
		jsts_geom_b,
		intersected_geometries = [],
		jsts_collection;

	this._validateCrsWithList(geometryA, geometriesB);

	for (i = 0, ii = geometriesB.length; i < ii; i++)
	{
		jsts_geom_b = this._toJstsGeometry(geometriesB[i]);
		if (!jsts_geom_b.isEmpty())
		{
			jsts_geom_b = jstsOverlayOp.intersection(jsts_geom_a, jsts_geom_b);
			if (!jsts_geom_b.isEmpty())
			{
				intersected_geometries.push(jsts_geom_b);
			}
		}
	}

	if (intersected_geometries.length > 0)
	{
		jsts_collection = this._jtsGeometryFactory.createGeometryCollection(intersected_geometries);
		ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_collection));
		ret.setCRS(geometryA.getCRS());

		// jsts_geom_a = jsts_collection.union();
		// if (!jsts_geom_a.isEmpty())
		// {
		// 	ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_geom_a));
		// 	ret.setCRS(geometryA.getCRS());
		// }
	}

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.symDifference = function(geometryA, geometryB)
{
	let ret = null,
		jsts_geom_a = this._toJstsGeometry(geometryA),
		jsts_geom_b = this._toJstsGeometry(geometryB);

	this._validateCrs(geometryA, geometryB);

	jsts_geom_a = jstsOverlayOp.symDifference(jsts_geom_a, jsts_geom_b);
	if (!jsts_geom_a.isEmpty())
	{
		ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_geom_a));
		ret.setCRS(geometryA.getCRS());
	}

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.symDifferenceList = function(geometryA, geometriesB)
{
	let ret = null,
		i, ii,
		jsts_geom_a = this._toJstsGeometry(geometryA),
		jsts_geom_b;

	this._validateCrsWithList(geometryA, geometriesB);

	for (i = 0, ii = geometriesB.length; i < ii; i++)
	{
		jsts_geom_b = this._toJstsGeometry(geometriesB[i]);
		jsts_geom_a = jstsOverlayOp.symDifference(jsts_geom_a, jsts_geom_b);
	}

	if (!jsts_geom_a.isEmpty())
	{
		ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_geom_a));
		ret.setCRS(geometryA.getCRS());
	}

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.union = function(geometryA, geometryB)
{
	let ret,
		jsts_geom_a = this._toJstsGeometry(geometryA),
		jsts_geom_b = this._toJstsGeometry(geometryB);

	this._validateCrs(geometryA, geometryB);

	jsts_geom_a = UnionOp.union(jsts_geom_a, jsts_geom_b);
	ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_geom_a));
	ret.setCRS(geometryA.getCRS());

	return ret;
};

/**
 * @inheritDoc
 */
gc.gis.geom.OLGeometryProcessor.prototype.cascadedUnion = function (geometries)
{
	let ret,
		jsts_geom_a,
		jsts_geom_b,
		i, ii;

	if (geometries.length === 0)
	{
		return null;
	}

	this._validateCrsList(geometries);

	// TODO: optimal wäre die Verwendung von CascadedPolygonUnion, ist aber aktuell unklar wie das geht mit JSTS und Nutzung ES6 Module
	jsts_geom_a = this._toJstsGeometry(geometries[0]);
	for (i = 1, ii = geometries.length; i < ii; i++)
	{
		jsts_geom_b = this._toJstsGeometry(geometries[i]);
		jsts_geom_a = UnionOp.union(jsts_geom_a, jsts_geom_b);
	}

	ret = this._geometryFactory.createGeometry(this._jtsParser.write(jsts_geom_a));
	ret.setCRS(geometries[0].getCRS());

	return ret;
};

/**
 * Erzeugt ein JSTS-Geometrie anhand einer gc.gis.geom.GCGeometry und liefert diese zurück.
 *
 * @param {gc.gis.geom.GCGeometry} geometry
 * @private
 */
gc.gis.geom.OLGeometryProcessor.prototype._toJstsGeometry = function (geometry)
{
	if (geometry instanceof gc.gis.geom.GCCircle)
	{
		geometry = geometry.toPolygon();
	}

	return this._jtsParser.read(geometry.getImpl());
};

/**
 * Erzeugt eine Liste mit JSTS-Geometrien anhand einer Menge von gc.gis.geom.GCGeometry und liefert es zurück.
 *
 * @param {gc.gis.geom.GCGeometry[]} geometries
 * @private
 */
gc.gis.geom.OLGeometryProcessor.prototype._toJstsGeometryList = function (geometries)
{
	const jsts_geoms = [];
	let i, ii;

	for (i = 0, ii = geometries.length; i < ii; i++)
	{
		jsts_geoms.push(this._toJstsGeometry(geometries[i]));
	}

	return jsts_geoms;
};

/**
 * Erzeugt ein JSTS-GeometrieCollection anhand einer Menge von gc.gis.geom.GCGeometry und liefert es zurück.
 *
 * @param {gc.gis.geom.GCGeometry[]} geometries
 * @private
 */
gc.gis.geom.OLGeometryProcessor.prototype._toJstsGeometryCollection = function (geometries)
{
	return this._jtsGeometryFactory.createGeometryCollection(this._toJstsGeometryList(geometries));
};

/**
 * Konvertiet ein JSTS-Point in ein OL-Coordinatenobjekt.
 *
 * @param {Object} p
 * @return {Array.<number>}
 * @private
 */
gc.gis.geom.OLGeometryProcessor.prototype._jstsPointToCoord = function(p)
{
	const c = [p.x, p.y];
	if (p.z)
	{
		c.push(p.z);
	}

	return c;
};

/**
 * Überpürft die Übereinstimmung der CRS zweier Geometrien und wirft ggf. eine Exception, wenn diese nicht
 * übereinstimmen.
 *
 * @param {gc.gis.geom.GCGeometry} geometryA
 * @param {gc.gis.geom.GCGeometry} geometryB
 * @private
 */
gc.gis.geom.OLGeometryProcessor.prototype._validateCrs = function (geometryA, geometryB)
{
	if (geometryA.getCRS() && geometryB.getCRS()
		&& geometryA.getCRS().toLowerCase() !== geometryB.getCRS().toLowerCase())
	{
		throw new gc.gis.error.GCError('Die Koordinatenreferenzsysteme der Geometrien stimmen nicht überein '
			+ '("' + geometryA.getCRS() + '" vs. "' + geometryB.getCRS() + '").');
	}
};

/**
 * Überpürft die Übereinstimmung der CRS einer Geometrie mit den CRS einer Liste von Geometrien und wirft ggf. eine
 * Exception, wenn diese nicht übereinstimmen.
 *
 * @param {gc.gis.geom.GCGeometry} geometryA
 * @param {gc.gis.geom.GCGeometry[]} geometriesB
 * @private
 */
gc.gis.geom.OLGeometryProcessor.prototype._validateCrsWithList = function (geometryA, geometriesB)
{
	for (let i = 1, ii = geometriesB.length; i < ii; i++)
	{
		this._validateCrs(geometryA, geometriesB[i]);
	}
};

/**
 * Überpürft die Übereinstimmung der CRS zu einer Menge von Geometrien.
 *
 * @param {gc.gis.geom.GCGeometry[]} geometries
 * @private
 */
gc.gis.geom.OLGeometryProcessor.prototype._validateCrsList = function (geometries)
{
	for (let i = 1, ii = geometries.length; i < ii; i++)
	{
		this._validateCrs(geometries[0], geometries[i]);
	}
};